import React from "react";
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from 'react-hot-toast';
import "@/App.css";

// Import i18n configuration
import "@/i18n/i18n";

// Import Auth Context
import { AuthProvider } from "@/contexts/AuthContext";

// Import Access Context for subscription checks
import { AccessProvider } from "@/contexts/AccessContext";

// Import Protected Route
import ProtectedRoute from "@/components/ProtectedRoute";

// Auth pages (keep)
import RegisterV2 from "@/components/v2/RegisterV2";
import LoginV2 from "@/components/v2/LoginV2";
import ForgotPasswordV2 from "@/components/v2/ForgotPasswordV2";
import ResetPasswordV2 from "@/components/v2/ResetPasswordV2";

// NEW Simplified components
import SimpleDashboard from "@/components/SimpleDashboard";
import SimpleGenerator from "@/components/SimpleGenerator";
import SimpleTasksPage from "@/components/SimpleTasksPage";
import SimpleDeviationsPage from "@/components/SimpleDeviationsPage";

// Legal pages
import { TermsOfService, PrivacyPolicy, Disclaimer } from "@/components/legal";

// Mobile PWA Components
import { MobileRUHApp, MobileRUHNew, MobileRUHDetail, MobileRUHLogin } from "@/components/mobile";
import MobileProtectedRoute from "@/components/mobile/MobileProtectedRoute";

// Legacy routes kept for backward-compat
import IKPackageViewer from "@/components/v2/IKPackageViewer";
import SubscriptionSuccess from "@/components/v2/SubscriptionSuccess";
import SubscriptionManagement from "@/components/v2/SubscriptionManagement";
import PricingPage from "@/components/v2/PricingPage";

// User Management
import UserManagement from "@/components/UserManagement";

// Reports
import ReportsPage from "@/components/ReportsPage";
import RiskAssessmentPage from "@/components/RiskAssessmentPage";

// System Admin
import SystemAdminLogin from "@/components/SystemAdminLogin";
import SystemAdminPanel from "@/components/SystemAdminPanel";

// Settings
import NotificationSettings from "@/pages/NotificationSettings";
import CompanyProfile from "@/pages/CompanyProfile";

// Analytics
import AnalyticsPage from "@/pages/AnalyticsPage";

// GAP Analysis
import GapAnalysisPage from "@/pages/GapAnalysisPage";

// Onboarding
import OnboardingPage from "@/pages/OnboardingPage";

// Mobile App
import MobileAppPage from "@/pages/MobileAppPage";

// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      <BrowserRouter>
        <AuthProvider>
          <AccessProvider>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/register" element={<RegisterV2 />} />
            <Route path="/login" element={<LoginV2 />} />
            <Route path="/system-login" element={<SystemAdminLogin />} />
            <Route path="/system-admin" element={<SystemAdminPanel />} />
            <Route path="/forgot-password" element={<ForgotPasswordV2 />} />
            <Route path="/reset-password" element={<ResetPasswordV2 />} />
            {/* Legal pages - public access */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            {/* Pricing - public access */}
            <Route path="/pricing" element={<PricingPage />} />
            
            {/* Mobile PWA Routes */}
            <Route path="/m/ruh/login" element={<MobileRUHLogin />} />
            <Route path="/m/ruh" element={<MobileProtectedRoute><MobileRUHApp /></MobileProtectedRoute>} />
            <Route path="/m/ruh/new" element={<MobileProtectedRoute><MobileRUHNew /></MobileProtectedRoute>} />
            <Route path="/m/ruh/:id" element={<MobileProtectedRoute><MobileRUHDetail /></MobileProtectedRoute>} />
            
            <Route path="/dashboard" element={<ProtectedRoute><SimpleDashboard /></ProtectedRoute>} />
            <Route path="/generate" element={<ProtectedRoute><SimpleGenerator /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><SimpleTasksPage /></ProtectedRoute>} />
            <Route path="/deviations" element={<ProtectedRoute><SimpleDeviationsPage /></ProtectedRoute>} />
            <Route path="/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/gap-analysis" element={<ProtectedRoute><GapAnalysisPage /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route path="/install" element={<MobileAppPage />} />
            <Route path="/risk" element={<ProtectedRoute><RiskAssessmentPage /></ProtectedRoute>} />
            <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettings /></ProtectedRoute>} />
            <Route path="/settings/company" element={<ProtectedRoute><CompanyProfile /></ProtectedRoute>} />
            <Route path="/ik-package/:packageId" element={<ProtectedRoute><IKPackageViewer /></ProtectedRoute>} />
            <Route path="/subscription" element={<ProtectedRoute><SubscriptionManagement /></ProtectedRoute>} />
            <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </AccessProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

// Landing Page Component - Premium Dark Theme
function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/60 backdrop-blur-2xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <img 
              src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
              alt="Systemklar" 
              className="h-9"
              data-testid="header-logo"
            />
            <nav className="hidden md:flex items-center gap-8">
              <Link 
                to="/pricing" 
                className="text-sm text-slate-300 hover:text-white transition-colors font-medium"
                data-testid="header-pricing-link"
              >
                Priser
              </Link>
              <a 
                href="#features" 
                className="text-sm text-slate-300 hover:text-white transition-colors font-medium"
              >
                Funksjoner
              </a>
              <a 
                href="#levels" 
                className="text-sm text-slate-300 hover:text-white transition-colors font-medium"
              >
                Nivåer
              </a>
            </nav>
            <div className="flex items-center gap-4">
              <Link 
                to="/login" 
                className="text-sm text-slate-300 hover:text-white transition-colors"
                data-testid="existing-customer-login"
              >
                Logg inn
              </Link>
              <button
                onClick={() => navigate('/register')}
                className="px-5 py-2.5 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-lg transition-all shadow-[0_0_20px_rgba(34,211,238,0.3)] hover:shadow-[0_0_30px_rgba(34,211,238,0.5)]"
                data-testid="header-cta"
              >
                Kom i gang
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full mb-8">
            <span className="text-cyan-400 text-sm font-medium tracking-wide">
              ✨ AI-drevet HMS-plattform for norske SMB
            </span>
          </div>
          
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold text-white leading-[1.1] tracking-tight mb-6">
            Fra krav til kontroll —
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-300">
              på minutter, ikke uker
            </span>
          </h1>
          
          <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-6 leading-relaxed">
            Systemklar gir deg et ferdig internkontrollsystem og guider deg steg for steg — fra minimumskrav til full kontroll og sertifisering.
          </p>

          {/* Audience line */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-10 text-sm text-slate-300">
            <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-3.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span>Laget for små og mellomstore bedrifter uten eget HMS-apparat</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-lg font-bold rounded-xl transition-all shadow-[0_0_40px_rgba(34,211,238,0.4)] hover:shadow-[0_0_60px_rgba(34,211,238,0.6)] hover:-translate-y-0.5"
              data-testid="hero-cta-button"
            >
              Start gratis — få kontroll på minutter
            </button>
            <Link
              to="/pricing"
              className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white text-lg font-medium rounded-xl backdrop-blur-sm transition-all border border-white/10"
              data-testid="hero-pricing-link"
            >
              Se priser →
            </Link>
          </div>
          <p className="text-sm text-slate-400 mb-12">Ingen binding. Ingen risiko.</p>

          {/* Trust indicators */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-200">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Ingen kredittkort</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Ingen binding</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>GDPR-sikker</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-cyan-400 text-xs font-bold tracking-[0.2em] uppercase mb-4 block">Funksjoner</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4">
              Alt du trenger — samlet ett sted
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Fra ukentlig oppfølging til full sertifisering. Resultater, ikke moduler.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Large feature card */}
            <div className="md:col-span-7 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-cyan-500/30 transition-all group">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-heading text-2xl font-medium text-white mb-3">Ferdig internkontrollsystem på minutter</h3>
              <p className="text-slate-300 leading-relaxed">
                Svar på noen enkle spørsmål om bedriften din, og få et komplett system tilpasset din bransje — klart til bruk umiddelbart.
              </p>
            </div>

            {/* Small feature cards */}
            <div className="md:col-span-5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-amber-500/30 transition-all group">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="font-heading text-2xl font-medium text-white mb-3">Full kontroll på avvik</h3>
              <p className="text-slate-400 leading-relaxed">
                Registrer, følg opp og lukk avvik uten å miste oversikten. Alt sporbart og klart for tilsyn.
              </p>
            </div>

            <div className="md:col-span-5 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-emerald-500/30 transition-all group">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-heading text-2xl font-medium text-white mb-3">Klar for tilsyn og sertifisering</h3>
              <p className="text-slate-300 leading-relaxed">
                Et system som vokser med deg — fra minimumskrav til Miljøfyrtårn eller ISO, uten å begynne på nytt.
              </p>
            </div>

            <div className="md:col-span-7 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 hover:border-blue-500/30 transition-all group">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-heading text-2xl font-medium text-white mb-3">All dokumentasjon samlet ett sted</h3>
              <p className="text-slate-300 leading-relaxed">
                Ukentlige oppgaver, årshjul og dokumenter på ett sted. Du vet alltid hva som må gjøres — og når.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Levels Section */}
      <section id="levels" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-cyan-400 text-xs font-bold tracking-[0.2em] uppercase mb-4 block">Din reise</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4">
              Én plattform. Tre nivåer.
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Start der du er. Gå så langt du vil. Samme plattform følger deg hele veien.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Level 1 */}
            <div className="relative bg-white/5 backdrop-blur-xl border border-amber-500/20 rounded-2xl p-8 hover:border-amber-500/40 transition-all" data-testid="level-1-card">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-300 text-xs font-semibold mb-4">
                <span className="w-2 h-2 rounded-full bg-amber-400"></span>
                Nivå 1
              </div>
              <h3 className="font-heading text-2xl font-medium text-white mb-3">Oppfyll kravene</h3>
              <p className="text-slate-300 leading-relaxed mb-4">
                Få nødvendig dokumentasjon på plass raskt og enkelt — uten å drukne i paragrafer.
              </p>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Ferdig internkontrollsystem
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Alle minimumskrav dekket
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Enkelt språk — ingen paragrafjungel
                </li>
              </ul>
            </div>

            {/* Level 2 */}
            <div className="relative bg-white/5 backdrop-blur-xl border border-emerald-500/20 rounded-2xl p-8 hover:border-emerald-500/40 transition-all" data-testid="level-2-card">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-300 text-xs font-semibold mb-4">
                <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                Nivå 2
              </div>
              <h3 className="font-heading text-2xl font-medium text-white mb-3">Få kontroll i praksis</h3>
              <p className="text-slate-300 leading-relaxed mb-4">
                Ikke bare dokumenter — følg opp, lukk avvik og hold internkontrollen levende uke for uke.
              </p>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Ukentlig oppgavestyring
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Avvik registreres og lukkes sporbart
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Alltid klar for tilsyn
                </li>
              </ul>
            </div>

            {/* Level 3 */}
            <div className="relative bg-white/5 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-8 hover:border-cyan-500/40 transition-all" data-testid="level-3-card">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-300 text-xs font-semibold mb-4">
                <span className="w-2 h-2 rounded-full bg-cyan-400"></span>
                Nivå 3
              </div>
              <h3 className="font-heading text-2xl font-medium text-white mb-3">Bli sertifisert</h3>
              <p className="text-slate-300 leading-relaxed mb-4">
                Bygg et system som er klart for Miljøfyrtårn eller ISO — når du er klar, ikke før.
              </p>
              <ul className="space-y-2 text-sm text-slate-400">
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Miljøfyrtårn-klar
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  ISO 9001, 14001 og 45001
                </li>
                <li className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  GAP-analyse mot 2026-krav
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pain + Gain Section */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-cyan-400 text-xs font-bold tracking-[0.2em] uppercase mb-4 block">Verdien</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4">
              Slipp kaos og usikkerhet rundt HMS
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              De fleste SMB-eiere kjenner det: bunken med HMS-permer, uklare krav og følelsen av å aldri være helt klar. Vi fjerner den.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { title: 'Slipp dager og uker med dokumentasjon', desc: 'AI genererer det du trenger på minutter — ikke over helger og kvelder.' },
              { title: 'Unngå usikkerhet ved tilsyn', desc: 'Du vet nøyaktig hvor ting står og kan dokumentere det når som helst.' },
              { title: 'Få kontroll på hva som faktisk må gjøres', desc: 'Ukentlige oppgaver viser deg én ting om gangen — i riktig rekkefølge.' },
              { title: 'Samle alt på ett sted', desc: 'Slutt å lete i permer, e-post og gamle Word-filer. Ett sted. Alltid oppdatert.' }
            ].map((item, i) => (
              <div key={i} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-6 hover:border-cyan-500/30 transition-all flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 bg-cyan-500/10 border border-cyan-500/20 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-heading text-lg font-medium text-white mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust signals */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-2xl md:text-3xl font-bold text-white mb-2">Norsk SMB</div>
              <div className="text-sm text-slate-300">Laget for små og mellomstore bedrifter</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-white mb-2">🇳🇴</div>
              <div className="text-sm text-slate-300">Norsk løsning og support</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-white mb-2">GDPR</div>
              <div className="text-sm text-slate-300">Trygg og sikker datahåndtering</div>
            </div>
            <div>
              <div className="text-2xl md:text-3xl font-bold text-white mb-2">Norsk lov</div>
              <div className="text-sm text-slate-300">Bygget på norsk regelverk</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-cyan-500/5 backdrop-blur-xl border border-cyan-500/20 rounded-3xl p-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
              Klar til å få kontroll?
            </h2>
            <p className="text-slate-300 mb-8 max-w-xl mx-auto">
              Start gratis i dag. Se selv hvor enkelt det kan være å gå fra krav til kontroll.
            </p>
            <button
              onClick={() => navigate('/register')}
              className="px-10 py-5 bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xl font-bold rounded-xl transition-all shadow-[0_0_40px_rgba(34,211,238,0.4)] hover:shadow-[0_0_60px_rgba(34,211,238,0.6)] hover:-translate-y-1"
              data-testid="final-cta-button"
            >
              Start gratis — få kontroll på minutter
            </button>
            <p className="text-sm text-slate-400 mt-4">Ingen binding. Ingen risiko.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/k46v5h98_Systemklar%20Logo%20med%20Sjekkmerke.png" 
                alt="Systemklar" 
                className="h-8"
              />
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <Link to="/pricing" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-pricing">
                Priser
              </Link>
              <Link to="/terms" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-terms">
                Vilkår
              </Link>
              <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors" data-testid="footer-privacy">
                Personvern
              </Link>
              <Link to="/disclaimer" className="text-slate-400 hover:text-white transition-colors">
                Ansvarsfraskrivelse
              </Link>
              <Link to="/system-login" className="text-slate-600 hover:text-slate-400 transition-colors">
                Admin
              </Link>
            </nav>
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_internkontroll/artifacts/eui6rlb9_Hovedlogo%20Valoria%20%28uten%20bakgrunn%29.png" 
                alt="Valoria Solutions" 
                className="h-5 opacity-60"
              />
              <span className="text-xs text-slate-500">© 2026 Valoria Solutions AS</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;